/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { URL_LANGUAGES } from "@/lib/constants";

/**
 * Returns the clean, self-referencing canonical URL for the current page:
 * origin + pathname only, with the query string and hash fragment removed.
 *
 * Why this matters for SEO: using the raw `window.location.href` (which keeps
 * `?utm_source=…`, `?ref=…`, `?standalone=true`, `#section`, etc.) makes every
 * tracked or fragment-suffixed variant of a page advertise itself as a distinct
 * canonical. Search engines then treat them as separate URLs, splitting ranking
 * signals across duplicates and wasting crawl budget. A clean self-referencing
 * canonical consolidates all variants onto the one real page.
 *
 * The pathname (including any locale prefix such as `/de`, `/es`) is preserved
 * unchanged, so each localised page correctly self-canonicalises to itself
 * rather than collapsing onto the English URL.
 *
 * @param href - URL to canonicalise. Defaults to the current page URL.
 * @returns origin + pathname, without query string or hash.
 */
/**
 * Old addresses that still resolve because links published years ago point at
 * them. They serve exactly the same page as their current address, so the
 * canonical has to name the current one. Left self-referencing, each old
 * address advertises itself as a separate page with identical content, and the
 * two compete with each other in search results.
 *
 * Keys are lowercase and carry no locale prefix — the prefix on the address
 * being canonicalised is preserved, so /de/contact_us1.aspx resolves to
 * /de/contact-us.
 */
const LEGACY_PATH_ALIASES: Readonly<Record<string, string>> = {
  "/contact_us1.aspx": "/contact-us",
};

/** Maps an old address onto its current one, keeping any locale prefix. */
function resolveLegacyPath(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  const hasLocale =
    parts.length > 0 &&
    (URL_LANGUAGES as readonly string[]).includes(parts[0].toLowerCase());
  const prefix = hasLocale ? `/${parts[0]}` : "";
  const rest = `/${(hasLocale ? parts.slice(1) : parts).join("/")}`;

  const current = LEGACY_PATH_ALIASES[rest.toLowerCase()];
  return current ? `${prefix}${current}` : pathname;
}

export function getCanonicalUrl(href: string = window.location.href): string {
  try {
    const url = new URL(href);
    return `${url.origin}${resolveLegacyPath(url.pathname)}`;
  } catch {
    // A malformed href must never break the rendered <link rel="canonical">.
    // Returning the input unchanged is safer than throwing during render.
    return href;
  }
}

/**
 * Builds the full list of hreflang alternate link descriptors for the current page.
 *
 * Returns one entry for each supported locale (x-default, en, de, es, fr, it, pt),
 * all pointing to the correct prefixed URL derived from the current pathname.
 *
 * Rules:
 *   • English ("en") and x-default → no URL prefix: /screen-recorder
 *   • Other locales → /{lang}/screen-recorder
 *
 * The `rest` segment is derived by normalizeLocalizedPath — any existing lang
 * prefix is stripped before rebuilding, so /de/screen-recorder → fr →
 * /fr/screen-recorder (never /fr/de/screen-recorder).
 *
 * @param pathname  Current window.location.pathname (injectable for testing).
 * @param origin    Current window.location.origin (injectable for testing).
 * @param rest      Pre-computed rest segment — pass when normalizeLocalizedPath
 *                  has already run (avoids double-calling in the same render).
 */
export interface HreflangEntry {
  hrefLang: string;
  href: string;
}

export function getHreflangAlternates(
  pathname: string,
  origin: string,
  urlLanguages: readonly string[],
): HreflangEntry[] {
  // Derive the locale-stripped path segment.
  // We replicate the split logic from normalizeLocalizedPath inline here to
  // keep canonicalUrl.ts free of cross-module imports at the utility level.
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0];
  const hasLangPrefix =
    Boolean(first) && urlLanguages.includes(first as string);
  const rest = (hasLangPrefix ? parts.slice(1) : parts).join("/");
  const basePath = `/${rest}`; // e.g. /screen-recorder or /
  const enUrl = `${origin}${basePath}`; // English (no prefix)

  const results: HreflangEntry[] = [
    { hrefLang: "x-default", href: enUrl },
    { hrefLang: "en", href: enUrl },
  ];

  for (const lang of urlLanguages) {
    results.push({ hrefLang: lang, href: `${origin}/${lang}${basePath}` });
  }

  return results;
}
